import React, { useCallback, useMemo, useRef, useState } from "react";

import type { GridReadyEvent, IServerSideGetRowsParams } from "ag-grid-community";
import { useNavigate } from "@tanstack/react-router";

import { getExcelReportProjects } from "@synthesis/shared-services/project-gge/projectController/client";
import { search } from "@synthesis/shared-services/project-gge/projectSearchController/client";
import type { ProjectSearchDto } from "@synthesis/shared-services/project-gge/projectSearchController/types";
import { downloadFile } from "../../../shared/utils";
import { IconClose, IconFilterOff, IconPlus, IconProjectListReport } from "@synthesis/shared-icons";
import { Button, SearchBar, Table } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { useUserRolesGGE } from "../../hooks";

import { getTableColumns } from "./projects-page.columns";

import { DEFAULT_PROJECTS_PAGE_SIZE, getProjectsSearchPage, PROJECTS_SORT_FIELD_MAP, type SearchDto } from "./projects-page.lib";

const FILTER_NAME_MAP: Record<string, string> = {
  TITLE: "Наименование проекта",
  ORGANIZATION: "Организация проекта",
  EXTERNAL_ID: "Номер проекта экспертизы",
  CREATED_AT: "Дата создания",
};

type FilterThumbnailProps = {
  thumb: string;
  onClear: (filterKey: string) => void;
};

const FilterThumbnail = ({ thumb, onClear }: FilterThumbnailProps) => (
  <div className="border-neutrals-90 bg-neutrals-99 paragraph-sm hover:bg-tertiary-99 flex cursor-pointer items-center gap-2 rounded-md border-2 px-2 py-1.5">
    <IconFilterOff className="size-5 shrink-0" />
    <span>{FILTER_NAME_MAP[thumb]}</span>
    <div onClick={() => onClear(thumb)}>
      <IconClose className="size-5 shrink-0" />
    </div>
  </div>
);

export const ProjectsPage = ({ isArchived }: { isArchived?: boolean }) => {
  const navigate = useNavigate();
  const gridApi = React.useRef<{ api: unknown } | null>(null);
  const bodySearchRef = useRef<ProjectSearchDto | null>(null);
  const [searchValue, setSearchValue] = useState<null | string>(null);
  const [paginationState, setPaginationState] = useState({ totalProjects: 0, filteredProjects: 0 });
  const [isShouldClearSearch, setIsShouldClearSearch] = useState(false);
  const [searchPatternState, setSearchPatternState] = useState<SearchDto | Record<string, never>>({});
  const roles = useUserRolesGGE();

  const tableColumns = useMemo(() => getTableColumns(searchValue, searchPatternState, setSearchPatternState), [searchValue, searchPatternState]);

  const getProjectReport = async () => {
    try {
      if (!bodySearchRef.current) return;
      const body = {
        searchPatterns: bodySearchRef.current.searchPatterns,
        size: paginationState.filteredProjects,
        page: 1,
      };
      const data = (await getExcelReportProjects({ body, query: { isArchive: !!isArchived }, responseType: "blob" })).data;
      if (!(data instanceof Blob)) throw new Error("Unexpected report response format");
      downloadFile(data, "Проектный отчёт");
    } catch (e) {
      logger.error(e);
    }
  };

  const handleRowClick = useCallback(
    (event: { data?: { id: string; isArchived: boolean } }) => {
      const data = event.data;
      if (!data) return;
      if (data.isArchived) {
        navigate({ to: "/archiveProject/$projectId/dashboard", params: { projectId: data.id } });
      } else {
        navigate({ to: "/project/$projectId/dashboard", params: { projectId: data.id } });
      }
    },
    [navigate],
  );

  const serverSideDatasource = useMemo(() => {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        const { startRow, endRow, sortModel } = params.request;
        const sortItem = sortModel[0];
        const requestedBlockSize = startRow != null && endRow != null && endRow > startRow ? endRow - startRow : undefined;
        const pageSize = Math.max(1, params.api.paginationGetPageSize() || requestedBlockSize || DEFAULT_PROJECTS_PAGE_SIZE);
        const page = getProjectsSearchPage(startRow, pageSize);

        try {
          const searchPatternFromSearchValue = searchValue
            ? {
                MAIN_TITLE: {
                  value: [searchValue],
                  pattern: "CONTAINS",
                },
              }
            : {};

          const withSearchStateAndSearchValue = !searchValue
            ? searchPatternState
            : {
                ...searchPatternState,
                ...(searchPatternState.MAIN_TITLE
                  ? {}
                  : {
                      MAIN_TITLE: {
                        value: [searchValue],
                        pattern: "CONTAINS",
                      },
                    }),
              };

          const searchPatterns = Object.entries(searchPatternState).length > 0 ? withSearchStateAndSearchValue : searchPatternFromSearchValue;

          const body: ProjectSearchDto = {
            searchPatterns: searchPatterns as SearchDto,
            page,
            size: pageSize,
          };

          if (sortItem) {
            const sortBy = PROJECTS_SORT_FIELD_MAP[sortItem.colId];

            if (sortBy) {
              body.sortBy = sortBy;
              body.sortDirection = sortItem.sort === "desc" ? "DESC" : "ASC";
            }
          }

          bodySearchRef.current = body;
          const data = (await search({ body, query: { isArchive: !!isArchived } })).data;
          const totalProjects = data.totalProjects || 0;
          const projects = data.projectDto || [];

          const rowData = projects.map((project) => {
            const metaExternalId = project?.meta?.find((meta) => meta.key === "externalId");
            return {
              id: project?.id ?? "",
              meta: metaExternalId?.value ?? "",
              title: project.title ?? "",
              objectName: project.objectName ?? "",
              date: project.createdAt ?? "",
              isArchived: project.isArchived ?? false,
            };
          });

          setPaginationState({ totalProjects, filteredProjects: data.filteredProjects || 0 });
          params.success({
            rowData,
            rowCount: data.filteredProjects ?? 0,
          });
        } catch (error) {
          logger.error("Error fetching data:", error);
          params.fail();
        }
      },
    };
  }, [searchPatternState, searchValue, isArchived]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApi.current = params;
  }, []);

  const onSearchProjects = useCallback((value: string) => {
    setIsShouldClearSearch(false);
    if (value === "") {
      setSearchValue(null);
      return;
    }
    setSearchValue(value);
  }, []);

  const handleReset = () => {
    setSearchPatternState({});
    setSearchValue(null);
    setIsShouldClearSearch(true);
  };

  const clearFilter = useCallback((filterKey: string) => {
    setSearchPatternState((prevState) => {
      const newState = { ...prevState };
      delete newState[filterKey];
      return newState;
    });
  }, []);

  const projectsDataLength =
    paginationState.filteredProjects < paginationState.totalProjects ? (
      <>
        {paginationState.filteredProjects} <span className="caption-sm"> из </span> {paginationState.totalProjects}
      </>
    ) : (
      <span>{paginationState.totalProjects}</span>
    );

  const thumbnails =
    searchPatternState &&
    Object.keys(searchPatternState)
      .filter((sortingColumn) => sortingColumn !== "MAIN_TITLE")
      .map((sortingColumn) => <FilterThumbnail key={sortingColumn} thumb={sortingColumn} onClear={clearFilter} />);

  return (
    <main className="relative flex h-full flex-col">
      <header className="flex flex-col gap-6 px-8 pb-6 pt-8">
        <h1 className="heading-h4">Проекты</h1>

        <div className="flex items-center justify-between gap-4">
          <SearchBar size="sm" clearable onChange={onSearchProjects} clearInput={isShouldClearSearch} />
          <div className="ml-auto flex gap-4">
            {(roles.АДМИНИСТРИРОВАНИЕ || roles.СУПЕР) && (
              <Button size="sm" leftIcon={<IconProjectListReport className="size-5" />} onClick={getProjectReport}>
                Проектный отчёт
              </Button>
            )}
            {(roles.АДМИНИСТРИРОВАНИЕ || roles.СУПЕР || roles.МОДЕРИРОВАНИЕ) && (
              <Button
                disabled={isArchived}
                leftIcon={<IconPlus className="size-5" />}
                size="sm"
                onClick={() => navigate({ to: "/projects/add-project" })}
                theme="primary"
              >
                Добавить проект экспертизы
              </Button>
            )}
          </div>
        </div>

        <p className="flex items-center gap-1 pt-2">
          <span className="caption-sm">найдено</span>
          {projectsDataLength}
        </p>
        {paginationState.filteredProjects < paginationState.totalProjects && Object.entries(searchPatternState).length > 0 && (
          <div className="flex gap-4">
            {thumbnails}
            <div
              onClick={handleReset}
              className="paragraph-sm hover:bg-tertiary-99 flex cursor-pointer items-center gap-2 px-2 py-1.5 hover:rounded-md"
            >
              <IconClose className="size-5" />
              <span>Сбросить</span>
            </div>
          </div>
        )}
      </header>

      <section className="flex min-h-0 grow">
        <div className="flex size-full min-h-0 grow flex-col px-8 pb-8">
          <Table
            className="block h-full w-full"
            isSortable
            hasFilters
            tableId="gge-project-page-table"
            tableColumns={tableColumns as never}
            rowModelType="serverSide"
            serverSideDatasource={serverSideDatasource}
            pagination
            paginationPageSize={DEFAULT_PROJECTS_PAGE_SIZE}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            groupDefaultExpanded={-1}
            onRowClicked={handleRowClick as never}
            onGridReady={onGridReady}
            popupParent={document.body}
            module="gge"
          />
        </div>
      </section>
    </main>
  );
};
